import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Loader2, Plus, Trash2, Wand2 } from 'lucide-react';
import { fetchMappingVersions, fetchSampleFromUrl, fetchTemplate, fetchUnmappedValues, RequestError, saveMappingDraft, searchTemplates, type MappingVersionsState, type UnmappedValuesResult } from '../../api/dataApi';
import { useWideLayout } from '../../layouts/AppLayout';
import { useStore } from '../../store/StoreContext';
import { useChannelTemplates } from '../../lib/useChannelTemplates';
import type { Template } from '../../types';
import { AppCard } from '../ui/AppCard';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { TextArea } from '../ui/TextField';
import { MappingVisualPreview } from '../../screens/integrations/MappingVisualPreview';
import type { JsonValue, MappingRule, MappingSpec } from '../../lib/mappingEngine';
import { distinctValues } from '../../lib/signalSplit';
import { detectSampleFormat, parseSample, samplePlaceholder, previewMappingPerOutput, SYSTEM_SAMPLE, type PreviewRecordResult, type SampleFormat } from '../../lib/mappingPreview';
import {
  addOutput,
  flattenPaths,
  getOutputs,
  isEditableSpec,
  linesToMap,
  mapToLines,
  moveRule,
  patchRule,
  removeOutput,
  setOutputRules,
  setOutputRecordPath,
  setStaticLabels,
  setTransform,
  summarizePreview,
  type SourcePath,
} from '../../lib/mappingSpecEdit';
import type { Mapping } from '../../types';
import { usePersistedState, oneOf } from '../../lib/usePersistedState';
import { wrapAtPath } from '../../lib/wrapAtPath';
import { docPathToFrom, fromToDocPath, listContaining } from '../../lib/jsonTree';
import { MetricChecklist } from './MetricChecklist';
import { SignalTable } from './SignalTable';
import { buildSignalRows, matchesRow, transformSummary } from '../../lib/signalTable';
import { findSiblingMetrics } from '../../lib/metricSiblings';
import { METRICS_PER_INTEGRATION, addMetricOutputs } from '../../lib/aiPlan';
import { buildMapTargets, labelKeyFor } from '../../lib/mapTargets';
import { JsonTree } from './JsonTree';
import { TransformEditor } from './TransformEditor';
import { OpsEditor } from './OpsEditor';

export const PREVIEWABLE = ['json', 'csv', 'tsv', 'xml', 'yaml', 'lines', 'webhook', 'system'];
const paneTitle = 'text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40';
const inputCls =
  'w-full rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-2 py-1 text-[12px] font-mono outline-none focus:border-blue-500';
const selectCls =
  'w-full rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-2 py-1 text-[12px] outline-none focus:border-blue-500';
type Sel = { output: number; rule: number } | null;
type Tab = 'source' | 'builder' | 'preview';
// What each `to:` value means, in plain words - also the labelled options
// the target dropdown offers (custom.* is "Label", added via its own key input).
const TARGET_META: Record<string, string> = {
  name: 'Signal name',
  value: 'Value',
  at: 'Time',
  signalRef: 'Signal ref (existing channel signal)',
};
const MAX_SAMPLE_BYTES = 2 * 1024 * 1024;
// Presets for the free-format timestamp box; anything else typed is a pattern.
const TIMESTAMP_PRESETS = ['rfc3339', 'unix_s', 'unix_ms', 'DD.MM.YYYY HH:II:ss', 'DD.MM.YY HH:II:ss', 'YYYY-MM-DD HH:mm:ss', 'MM/DD/YYYY HH:mm:ss'];

// Visual Mapping editor (roadmap #9 Phase 1, slice 6): source sample /
// signal builder / live preview. Edits a DRAFT of the active spec - saving
// changes nothing live; publishing stays in VersionsSection below it. Only
// ever writes rule fields it models, merged over the untouched spec
// (lib/mappingSpecEdit); a spec it can't model losslessly is shown read-only
// with a pointer to the raw JSON. Evaluation is entirely the parity-tested
// mappingEngine/mappingPreview, so the preview is the same code path as the
// browser Advanced editor's.
export function MappingVisualEditor({ mapping, onDraftSaved }: { mapping: Mapping; onDraftSaved?: () => void }) {
  useWideLayout(true);
  const nickname = mapping.nickname;
  const rawSourceType = mapping.sourceType.trim().toLowerCase();
  // A Scraper integration (scrape_json, scrape_csv...) is edited like the same format arriving any other way.
  const scrapeFormat = rawSourceType.startsWith('scrape_') ? rawSourceType.slice('scrape_'.length) : '';
  const sourceType = PREVIEWABLE.includes(scrapeFormat) ? scrapeFormat : rawSourceType;
  const { channels, updateChannel } = useStore();

  const [state, setState] = useState<MappingVersionsState | null>(null);
  const [loadError, setLoadError] = useState('');
  const [spec, setSpec] = useState<MappingSpec>({});
  const [baseline, setBaseline] = useState('');
  // A host-metrics mapping has no file to paste: seed the box with what one run hands the rules.
  const [sampleText, setSampleText] = useState(sourceType === 'system' ? SYSTEM_SAMPLE : '');
  const [recordIdx, setRecordIdx] = useState(0);
  const [fileName, setFileName] = useState('');
  const [format, setFormat] = useState<'auto' | SampleFormat>('auto');
  const [fileError, setFileError] = useState('');
  const [fetching, setFetching] = useState(false);
  // Left pane: the whole JSON as a filterable tree (default) or the raw text.
  const [view, setView] = useState<'tree' | 'text'>('tree');
  // A value clicked in the tree, waiting for the user to say where it goes.
  const [treePick, setTreePick] = useState<{ path: string; value: JsonValue; rel: string | null } | null>(null);
  const [treeChoice, setTreeChoice] = useState('');
  const [active, setActive] = useState<Sel>(null);
  const [builderMode, setBuilderMode] = usePersistedState<'simple' | 'advanced'>('mappingEditor.builderMode', 'simple', oneOf(['simple', 'advanced'] as const));
  const [tab, setTab] = usePersistedState<Tab>('mappingEditor.tab', 'builder', oneOf(['source', 'builder', 'preview'] as const));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [savedNote, setSavedNote] = useState(false);
  // Bumped on every (re)load so uncontrolled-text children remount with the loaded spec.
  const [epoch, setEpoch] = useState(0);

  const load = useCallback(async () => {
    try {
      const s = await fetchMappingVersions(nickname);
      const base = (s.draft ? s.draft.spec : s.activeSpec) as MappingSpec;
      setState(s);
      setSpec(base);
      setBaseline(JSON.stringify(base));
      setEpoch((n) => n + 1);
      setConflict(false);
      setError('');
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Unable to load the mapping.');
    }
  }, [nickname]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = state !== null && JSON.stringify(spec) !== baseline;

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const outputs = useMemo(() => getOutputs(spec), [spec]);

  // The "Signal ref" target's own dropdown offers every Signal you own, not
  // only ones already attached to the destination channel (same stage-3
  // widening as AutoMappingScreen's SignalPicker, applied here since this
  // editor is what every EXISTING mapping - including host-metrics/`system`
  // ones, see PREVIEWABLE above - is edited through). Picking one whose
  // template isn't attached yet auto-attaches it (attachTemplateForValue
  // below), same as the auto-map wizard does on create.
  const destChannel = channels.find((c) => c.nickname === (typeof spec.channel === 'string' ? spec.channel : ''));
  const destTemplates = useChannelTemplates(destChannel);
  const [ownedTemplates, setOwnedTemplates] = useState<Template[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const page = await searchTemplates('mine', '');
        const hydrated = (await Promise.all(page.data.map((n) => fetchTemplate(n)))).filter((t): t is Template => t !== null);
        if (!cancelled) setOwnedTemplates(hydrated);
      } catch {
        if (!cancelled) setOwnedTemplates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const allTemplates = useMemo(() => {
    const byNickname = new Map(ownedTemplates.map((t) => [t.nickname, t]));
    for (const t of destTemplates) byNickname.set(t.nickname, t);
    return [...byNickname.values()];
  }, [ownedTemplates, destTemplates]);
  const channelSignals = useMemo(
    () => allTemplates.flatMap((t) => t.signals.map((sg) => ({ name: sg.name, number: sg.number, emoji: sg.emoji }))),
    [allTemplates],
  );
  // The dropdown's option value -> the template it lives in, so picking it
  // can attach that template. Keyed by BOTH number and name: the "Signal
  // ref" dropdown's option value is the number (falling back to name only
  // when the signal has none yet), but the per-value map picker below
  // (rule.map, for a `name`-kind rule) always writes the plain name - one
  // lookup needs to answer to either.
  const templateForSignalValue = useMemo(() => {
    const out: Record<string, string> = {};
    for (const t of allTemplates) for (const sg of t.signals) {
      if (sg.number) out[sg.number] = t.nickname;
      out[sg.name] = t.nickname;
    }
    return out;
  }, [allTemplates]);
  const attachTemplateForValue = (value: string) => {
    const tplNickname = templateForSignalValue[value];
    if (!tplNickname || !destChannel || (destChannel.templateIds ?? []).includes(tplNickname)) return;
    void updateChannel({ ...destChannel, templateIds: [...(destChannel.templateIds ?? []), tplNickname] });
  };

  const { records, parseFailure } = useMemo(() => {
    if (sampleText.trim() === '') return { records: [], parseFailure: '' };
    try {
      const fmt = format === 'auto' ? detectSampleFormat(sourceType, sampleText, fileName, typeof spec.format === 'string' ? spec.format : undefined) : format;
      return { records: parseSample(sourceType, sampleText, spec.recordPath, fmt), parseFailure: '' };
    } catch (e) {
      return { records: [], parseFailure: e instanceof Error ? e.message : 'Could not parse that sample.' };
    }
  }, [sampleText, sourceType, spec.recordPath, spec.format, format, fileName]);

  // The sample as a document, when it is JSON - what the tree shows.
  const docValue = useMemo<JsonValue | null>(() => {
    const t = sampleText.trim();
    if (!t.startsWith('{') && !t.startsWith('[')) return null;
    try {
      return JSON.parse(t) as JsonValue;
    } catch {
      return null;
    }
  }, [sampleText]);
  const results: PreviewRecordResult[] | null = useMemo(
    () => (records.length ? previewMappingPerOutput(spec, records, docValue, nickname || 'sample') : null),
    [records, spec, nickname, docValue],
  );
  const summary = useMemo(() => (results ? summarizePreview(spec, results) : null), [results, spec]);
  const paths = useMemo(() => flattenPaths(records[Math.min(recordIdx, Math.max(records.length - 1, 0))]), [records, recordIdx]);

  const docIsList = Array.isArray(docValue);
  const recordPath = typeof spec.recordPath === 'string' ? spec.recordPath : '';
  const showTree = view === 'tree' && docValue !== null;
  // One document, many metrics: an output may read its OWN list (outputs[i].recordPath).
  const ownPath = (o: number): string => {
    const p = Array.isArray(spec.outputs) ? spec.outputs[o]?.recordPath : undefined;
    return typeof p === 'string' ? p : '';
  };
  const outPath = (o: number): string => ownPath(o) || recordPath;
  // Where a clicked value can feed output o: record-relative `rel` inside its list, or
  // - for an output with no field of its own yet (or already bound to a list) - the list the
  // value is in, which then becomes that output's own list (`bind`).
  const targetFor = (o: number, path: string): { rel: string; bind?: string } | null => {
    const rel = docPathToFrom(path, outPath(o), docIsList);
    if (rel !== null) return { rel };
    if (!docValue || !Array.isArray(spec.outputs) || !outputs[o]) return null;
    if (!ownPath(o) && outputs[o].some((r) => r.from)) return null; // its fields already read the mapping's list
    const found = listContaining(docValue, path);
    return found ? { rel: found.rel, bind: found.list } : null;
  };
  // Every metric list shaped like the one the mapping reads, found in the document.
  const siblingMetrics = useMemo(() => {
    if (!docValue || docIsList || !Array.isArray(spec.outputs) && !recordPath) return [];
    const repPath = ownPath(0) || recordPath;
    if (!repPath) return [];
    const valueFrom = outputs[0]?.find((r) => r.to === 'value')?.from;
    return findSiblingMetrics(docValue, repPath, valueFrom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docValue, spec, recordPath]);
  const readPaths = new Set(outputs.map((_, o) => outPath(o)));
  const addableMetrics = siblingMetrics.filter((m) => !readPaths.has(m.path));
  const [metricOff, setMetricOff] = useState<Record<string, boolean>>({});
  const [metricsOpen, setMetricsOpen] = useState(false);
  // A mapping with hundreds of outputs (one per metric) is browsed a page at a time, with a filter.
  const [outFilter, setOutFilter] = useState('');
  const [outPage, setOutPage] = useState(0);
  const [previewFilter, setPreviewFilter] = useState('');
  const [simpleSel, setSimpleSel] = useState<number | null>(null);
  const [signalSearch, setSignalSearch] = useState('');
  const [prepOpen, setPrepOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const filteredOutputs = useMemo(() => {
    const f = outFilter.trim().toLowerCase();
    const all = outputs.map((_, o) => o);
    if (!f) return all;
    return all.filter((o) => {
      const text = [ownPath(o), ...outputs[o].flatMap((r) => [r.to, r.from, typeof r.const === 'string' ? r.const : undefined])].join(' ').toLowerCase();
      return text.includes(f);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputs, outFilter, spec]);
  const pageCount = Math.max(1, Math.ceil(filteredOutputs.length / OUTPUTS_PER_PAGE));
  const page = Math.min(outPage, pageCount - 1);
  const pageOutputs = filteredOutputs.slice(page * OUTPUTS_PER_PAGE, (page + 1) * OUTPUTS_PER_PAGE);
  // Selecting a field (e.g. from the tree) in an output on another page brings that page up.
  const activeOutput = active?.output;
  useEffect(() => {
    if (activeOutput === undefined) return;
    const at = filteredOutputs.indexOf(activeOutput);
    setSimpleSel(activeOutput);
    if (at >= 0) setOutPage(Math.floor(at / OUTPUTS_PER_PAGE));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOutput]);
  // Selecting a field in the builder marks where it comes from in the document.
  const activeRule = active ? outputs[active.output]?.[active.rule] : undefined;
  const highlight = activeRule?.from ? fromToDocPath(activeRule.from, active ? outPath(active.output) : recordPath, Math.min(recordIdx, Math.max(records.length - 1, 0)), docIsList) : undefined;
  // A click in the tree opens a "Map to..." chooser (below the tree) instead of
  // acting at once: the user picks which field of the mapping the value feeds.
  // Inside the records it becomes a per-record `from`; outside (e.g. entityName
  // beside the list) it can only become fixed text, for a name or a label.
  const mapOptions = useMemo(
    () => (treePick ? buildMapTargets(outputs, treePick.path, (o) => targetFor(o, treePick.path) !== null) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [treePick, outputs, spec, docValue],
  );
  const pickFromTree = (path: string, value: JsonValue) => {
    const rel = docPathToFrom(path, recordPath, docIsList);
    const opts = buildMapTargets(outputs, path, (o) => targetFor(o, path) !== null);
    const preset = active ? `rule:${active.output}:${active.rule}` : '';
    setTreePick({ path, value, rel });
    // The selected rule is the obvious target - preselected when it can take this value.
    setTreeChoice(opts.some((o) => o.key === preset && !o.disabled) ? preset : '');
  };
  const applyTreePick = () => {
    if (!treePick || !treeChoice) return;
    const { path, value, rel } = treePick;
    const text = value === null ? '' : String(value);
    if (treeChoice.startsWith('rule:')) {
      const [, o, i] = treeChoice.split(':').map(Number) as [unknown, number, number];
      const t = targetFor(o, path);
      if (t) {
        const rules = outputs[o].map((r, idx) => (idx === i ? patchRule(r, { from: t.rel, const: undefined, ...(r.to === 'value' && !r.cast ? { cast: 'number' } : {}) }) : r));
        const next = setOutputRules(spec, o, rules);
        update(t.bind ? setOutputRecordPath(next, o, t.bind) : next);
      } else {
        setRule(o, i, { const: text, from: undefined });
      }
      setActive({ output: o, rule: i });
    } else if (treeChoice === 'new:labels') {
      const to = `labels.${labelKeyFor(path)}`;
      addRule(0, rel !== null ? { to, from: rel } : { to, const: text });
    } else if (treeChoice.startsWith('new:')) {
      const to = treeChoice.slice(4);
      addRule(0, rel !== null ? { to, from: rel, ...(to === 'value' ? { cast: 'number' } : {}) } : { to, const: text });
    }
    setTreePick(null);
    setTreeChoice('');
    setTab('builder');
  };

  if (!PREVIEWABLE.includes(sourceType)) return <MappingVisualPreview mapping={mapping} />;
  if (!state) {
    return loadError ? (
      <div className="flex flex-col gap-2">
        <p className="text-sm text-red-500">{loadError}</p>
        <MappingVisualPreview mapping={mapping} />
      </div>
    ) : (
      <div className="py-3 text-center text-sm text-black/40 dark:text-white/40">
        <Loader2 size={14} className="inline animate-spin" /> Loading editor…
      </div>
    );
  }
  if (!isEditableSpec(spec)) {
    return (
      <div className="flex flex-col gap-2">
        <p role="status" className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm m-0">
          This mapping uses rule options the visual editor can't edit without risking data loss, so it's read-only here.
          Use the Advanced editor or the draft JSON below to change it.
        </p>
        <MappingVisualPreview mapping={{ ...mapping, spec }} />
      </div>
    );
  }

  const update = (next: MappingSpec) => {
    setSpec(next);
    setSavedNote(false);
  };
  const setRules = (o: number, rules: MappingRule[]) => update(setOutputRules(spec, o, rules));
  const setRule = (o: number, i: number, patch: Partial<Record<keyof MappingRule, unknown>>) =>
    setRules(o, outputs[o].map((r, idx) => (idx === i ? patchRule(r, patch) : r)));
  const addRule = (o: number, rule: MappingRule) => {
    setRules(o, [...outputs[o], rule]);
    setActive({ output: o, rule: outputs[o].length });
  };

  const pickPath = (path: string) => {
    if (active && outputs[active.output]?.[active.rule]) {
      setRule(active.output, active.rule, { from: path });
    } else {
      const leaf = path.split('.').pop() ?? path;
      addRule(0, { to: `labels.${leaf}`, from: path });
    }
    setTab('builder');
  };

  // Reads a local file into the sample box - entirely in the browser, nothing
  // is uploaded. A .csv only makes sense for a csv source (a webhook never
  // receives CSV), so anything else is refused rather than mis-previewed.
  const loadFile = async (file: File | undefined) => {
    setFileError('');
    if (!file) return;
    if (file.size > MAX_SAMPLE_BYTES) return setFileError('That file is over 2 MB - paste a smaller excerpt.');
    setSampleText(await file.text());
    setFileName(file.name);
    setRecordIdx(0);
  };

  // A Scraper integration always has a URL: fetch the real data from it (via the
  // server - CORS) instead of pasting. A JSON response comes back as just the
  // list of records, so it is nested back under the mapping's record path.
  const scrapeTarget = PREVIEWABLE.includes(scrapeFormat) && typeof spec.target === 'string' ? spec.target.trim() : '';
  const canFetch = /^https?:\/\//i.test(scrapeTarget);
  const fetchFromUrl = async () => {
    setFetching(true);
    setFileError('');
    const res = await fetchSampleFromUrl(scrapeTarget, typeof spec.recordPath === 'string' && spec.recordPath ? spec.recordPath : undefined);
    setFetching(false);
    if (!res.sample) return setFileError(res.error ?? 'Could not fetch that URL.');
    const { text, format: fmt, recordPath } = res.sample;
    // A whole (pruned) document arrives as is. A bare list of records - the
    // response was itself a list, or only the records were returned - goes back
    // under the mapping's record path so that path resolves.
    let out = text;
    if (fmt === 'json' && recordPath) {
      try {
        const parsed: unknown = JSON.parse(text);
        if (Array.isArray(parsed)) out = JSON.stringify(wrapAtPath(recordPath, parsed), null, 2);
      } catch {
        /* keep the text as returned */
      }
    }
    setSampleText(out);
    setFileName(`fetched from ${scrapeTarget}`);
    setFormat('auto');
    setRecordIdx(0);
  };

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await saveMappingDraft(nickname, spec as Record<string, unknown>, state.activeVersion);
      setBaseline(JSON.stringify(spec));
      setSavedNote(true);
      onDraftSaved?.();
    } catch (e) {
      if (e instanceof RequestError && e.status === 409) setConflict(true);
      else setError(e instanceof Error ? e.message : 'Unable to save the draft.');
    } finally {
      setBusy(false);
    }
  };

  const staticLabels = (spec.staticLabels && typeof spec.staticLabels === 'object' ? spec.staticLabels : {}) as Record<string, string>;
  const pane = (t: Tab) => `${tab === t ? 'flex' : 'hidden'} xl:flex flex-col gap-2`;

  const renderOutput = (o: number) => {
    const rules = outputs[o];
    return (
            <div key={o} className="flex flex-col gap-2">
              {outputs.length > 1 && (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-medium text-black/40 dark:text-white/40 flex-1 min-w-0 truncate">
                  Output {o + 1}
                  {ownPath(o) && <span className="font-mono font-normal"> · reads {ownPath(o)}</span>}
                </span>
                  <button aria-label={`Remove output ${o + 1}`} onClick={() => update(removeOutput(spec, o))}>
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
              {rules.length === 0 && <p className="text-xs text-black/40 dark:text-white/40 m-0">No fields yet.</p>}
              {rules.map((rule, i) => (
                <RuleRow
                  key={`${epoch}-${i}`}
                  rule={rule}
                  paths={paths}
                  records={records}
                  channelSignals={channelSignals}
                  onPickChannelSignal={attachTemplateForValue}
                  selected={active?.output === o && active.rule === i}
                  onSelect={() => setActive({ output: o, rule: i })}
                  onPatch={(p) => setRule(o, i, p)}
                  onRemove={() => {
                    setRules(o, rules.filter((_, idx) => idx !== i));
                    setActive(null);
                  }}
                  onMove={(d) => setRules(o, moveRule(rules, i, i + d))}
                  canUp={i > 0}
                  canDown={i < rules.length - 1}
                />
              ))}
              <div className="flex flex-wrap gap-1.5">
                {(['name', 'value', 'at', 'signalRef'] as const)
                  .filter((t) => !rules.some((r) => r.to === t))
                  .map((t) => (
                    <Button
                      key={t}
                      variant="bordered"
                      onClick={() => addRule(o, t === 'at' ? { to: 'at', timestamp: 'rfc3339' } : { to: t })}
                    >
                      + {t === 'signalRef' ? 'signal ref' : t}
                    </Button>
                  ))}
                <Button variant="bordered" onClick={() => addRule(o, { to: 'labels.' })}>
                  + label
                </Button>
              </div>
            </div>
    );
  };

  const signalRows = buildSignalRows(spec, results, ownPath);
  const shownRows = signalRows.filter((r) => matchesRow(r, signalSearch));
  const firstError = signalRows.find((r) => r.status === 'error');
  // The first Signal's fields are open until another is picked.
  const selectedRow = outputs.length === 0 ? null : simpleSel !== null && simpleSel < outputs.length ? simpleSel : 0;

  const anyMapRule = outputs.some((rules) => rules.some((r) => (r.to === 'signalRef' || r.to === 'name') && r.map && Object.keys(r.map).length > 0));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-black/50 dark:text-white/50 flex-1 min-w-[12rem]">
          {state.draft ? `Editing your draft (from v${state.draft.baseVersion})` : `Editing a draft of v${state.activeVersion}`} - saving
          changes nothing live until you publish under Versions.
        </span>
        {dirty && <span className="text-xs text-amber-600 dark:text-amber-400">unsaved changes</span>}
        {savedNote && !dirty && <span className="text-xs text-emerald-600 dark:text-emerald-400">draft saved</span>}
        <Button variant="bordered" onClick={() => void load()} disabled={busy || (!dirty && !conflict)}>
          Discard edits
        </Button>
        <Button onClick={() => void save()} disabled={busy || !dirty}>
          {busy ? 'Saving…' : 'Save draft'}
        </Button>
      </div>
      {conflict && (
        <div role="alert" className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm flex items-center gap-2 flex-wrap">
          <span>This integration changed since you loaded it (a newer version was published).</span>
          <Button variant="bordered" onClick={() => void load()}>
            Reload
          </Button>
        </div>
      )}
      {error && <p role="alert" className="text-sm text-red-500 m-0">{error}</p>}

      <div role="tablist" className="flex gap-1 xl:hidden">
        {(['source', 'builder', 'preview'] as Tab[]).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={`flex-1 rounded-lg px-2 py-1.5 text-sm capitalize ${
              tab === t ? 'bg-blue-600 text-white' : 'bg-black/[0.05] dark:bg-white/[0.08]'
            }`}
          >
            {t === 'builder' ? 'Mapping' : t}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-4 xl:grid xl:grid-cols-3 xl:gap-4 xl:items-start">
        {/* Source sample */}
        <AppCard className={pane('source')} compact>
          <span className={paneTitle}>Source sample</span>
          <div className="flex flex-wrap items-center gap-2">
            <label className="cursor-pointer rounded-lg border border-black/15 dark:border-white/15 px-2.5 py-1 text-[12px] font-medium hover:bg-black/[0.04] dark:hover:bg-white/[0.06]">
              Load from file…
              <input
                type="file"
                aria-label="Load sample from file"
                accept=".json,.xml,.csv,.tsv,.yaml,.yml,.log,.txt"
                className="sr-only"
                onChange={(e) => {
                  void loadFile(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
            {canFetch && (
              <button
                type="button"
                onClick={() => void fetchFromUrl()}
                disabled={fetching}
                className="inline-flex items-center gap-1 rounded-lg border border-black/15 dark:border-white/15 px-2.5 py-1 text-[12px] font-medium hover:bg-black/[0.04] dark:hover:bg-white/[0.06] disabled:opacity-50"
              >
                {fetching ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
                Fetch data from URL
              </button>
            )}
            {fileName && <span className="text-[11px] text-black/45 dark:text-white/45 truncate max-w-[10rem]">{fileName}</span>}
            {sourceType === 'webhook' && (
              <select
                aria-label="Sample format"
                value={format}
                onChange={(e) => setFormat(e.target.value as 'auto' | SampleFormat)}
                className="ml-auto rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-1 py-1 text-[12px]"
              >
                <option value="auto">Auto-detect</option>
                <option value="json">JSON</option>
                <option value="xml">XML</option>
                <option value="csv">CSV</option>
                <option value="tsv">TSV</option>
                <option value="yaml">YAML</option>
                <option value="lines">Text lines</option>
              </select>
            )}
          </div>
          {fileError && <p role="alert" className="text-xs text-red-500 m-0">{fileError}</p>}
          {docValue !== null && (
            <div role="group" aria-label="Sample view" className="flex gap-1 text-[12px]">
              {(['tree', 'text'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={view === v}
                  onClick={() => setView(v)}
                  className={`rounded-lg px-2.5 py-0.5 font-medium ${view === v ? 'bg-blue-600/15 text-blue-700 dark:text-blue-300' : 'text-black/50 dark:text-white/50 hover:bg-black/[0.05] dark:hover:bg-white/[0.07]'}`}
                >
                  {v === 'tree' ? 'Tree' : 'Text'}
                </button>
              ))}
            </div>
          )}
          {showTree && docValue !== null && (
            <>
              <span className="text-[11px] text-black/40 dark:text-white/40">
                {active ? 'Click a value to use it as the selected field’s source.' : 'Click a value to add it as a label. Select a field on the right to see where it comes from.'}
              </span>
              <JsonTree doc={docValue} highlight={highlight} recordPath={active ? outPath(active.output) : recordPath} onPick={pickFromTree} />
              {treePick && (
                <div role="group" aria-label="Map this value" className="flex flex-col gap-1.5 rounded-xl border border-blue-500/40 bg-blue-600/5 p-2">
                  <span className="text-xs">
                    Map <code className="font-mono">{treePick.path.split('.').pop()}</code>
                    <span className="text-black/50 dark:text-white/50"> “{treePick.value === null ? 'null' : String(treePick.value)}”</span> to:
                  </span>
                  <select
                    aria-label="Map to"
                    value={treeChoice}
                    onChange={(e) => setTreeChoice(e.target.value)}
                    className="w-full rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-2 py-1 text-[12px] outline-none focus:border-blue-500"
                  >
                    <option value="">Choose where it goes…</option>
                    {mapOptions.map((o) => (
                      <option key={o.key} value={o.key} disabled={o.disabled}>
                        {o.label}
                        {o.hint ? ` (${o.hint})` : ''}
                      </option>
                    ))}
                  </select>
                  <div className="flex gap-1.5">
                    <Button onClick={applyTreePick} disabled={!treeChoice}>
                      Apply
                    </Button>
                    <Button variant="bordered" onClick={() => setTreePick(null)}>
                      Cancel
                    </Button>
                  </div>
                  {!outputs.some((_, o) => targetFor(o, treePick.path)) && (
                    <span className="text-[11px] text-amber-600 dark:text-amber-400">
                      This value sits outside the records, so it can't be read per record - it can only become fixed text for a name or a label.
                    </span>
                  )}
                </div>
              )}
              {addableMetrics.length > 0 && (
                <div role="group" aria-label="Add metrics" className="flex flex-col gap-1.5 rounded-xl border border-emerald-500/40 bg-emerald-600/5 p-2">
                  <span className="text-xs">
                    {addableMetrics.length} more metric{addableMetrics.length === 1 ? '' : 's'} in this document have the same shape as the one you map. Add them all, then untick the ones you don't need.
                  </span>
                  {!metricsOpen ? (
                    <Button onClick={() => setMetricsOpen(true)}>Add metrics…</Button>
                  ) : (
                    <>
                      <MetricChecklist metrics={addableMetrics} off={metricOff} onChange={setMetricOff} />
                      {(() => {
                        const chosen = addableMetrics.filter((m) => !metricOff[m.path]);
                        const take = chosen.slice(0, METRICS_PER_INTEGRATION - 1);
                        return (
                          <>
                            {chosen.length > take.length && (
                              <span className="text-[11px] text-amber-600 dark:text-amber-400">One integration holds at most {METRICS_PER_INTEGRATION} metrics - the first {take.length} are added; use the AI page for the rest.</span>
                            )}
                            <div className="flex gap-1.5">
                              <Button
                                disabled={take.length === 0}
                                onClick={() => {
                                  update(addMetricOutputs(spec, take));
                                  setMetricsOpen(false);
                                }}
                              >
                                Add {take.length} metric{take.length === 1 ? '' : 's'}
                              </Button>
                              <Button variant="bordered" onClick={() => setMetricsOpen(false)}>
                                Cancel
                              </Button>
                            </div>
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>
              )}
            </>
          )}
          {!showTree && (
            <TextArea
            aria-label="Sample payload"
            value={sampleText}
            onChange={(e) => {
              setSampleText(e.target.value);
              setFileName('');
              setRecordIdx(0);
            }}
            placeholder={samplePlaceholder(sourceType, typeof spec.format === 'string' ? spec.format : undefined)}
            className="font-mono text-[12px] min-h-[100px]"
            spellCheck={false}
          />
          )}
          {parseFailure && <p className="text-xs text-red-500 m-0">{parseFailure}</p>}
          {records.length > 1 && (
            <div className="flex items-center gap-2 text-xs">
              <Button variant="bordered" onClick={() => setRecordIdx((i) => Math.max(0, i - 1))} disabled={recordIdx === 0}>
                Prev
              </Button>
              <span>
                Record {Math.min(recordIdx, records.length - 1) + 1} of {records.length}
              </span>
              <Button variant="bordered" onClick={() => setRecordIdx((i) => Math.min(records.length - 1, i + 1))} disabled={recordIdx >= records.length - 1}>
                Next
              </Button>
            </div>
          )}
          {showTree ? null : paths.length > 0 ? (
            <div className="flex flex-col gap-0.5 max-h-72 overflow-y-auto">
              <span className="text-[11px] text-black/40 dark:text-white/40">
                {active ? 'Click a path to use it as the selected field’s source.' : 'Click a path to add it as a label.'}
              </span>
              {paths.map((p) => (
                <button
                  key={p.path}
                  onClick={() => pickPath(p.path)}
                  className="flex justify-between gap-2 rounded px-1.5 py-0.5 text-left text-[12px] font-mono hover:bg-blue-600/10"
                >
                  <span className="truncate">{p.path}</span>
                  <span className="truncate text-black/40 dark:text-white/40">{p.sample}</span>
                </button>
              ))}
            </div>
          ) : (
            !parseFailure && <p className="text-xs text-black/40 dark:text-white/40 m-0">Paste a sample to pick fields from it. Nothing is sent anywhere.</p>
          )}
        </AppCard>

        {/* Signal builder */}
        <AppCard className={pane('builder')} compact>
          <span className={paneTitle}>Signal builder</span>
          <h2 className="m-0 text-lg font-semibold">Signals out: {outputs.length === 1 ? '1 Signal' : `all ${outputs.length} at a glance`}</h2>
          <div className="flex flex-wrap items-center gap-2">
            <button
              aria-expanded={prepOpen}
              onClick={() => setPrepOpen((v) => !v)}
              className="rounded-lg border border-black/15 dark:border-white/15 px-2 py-1 text-[12px] text-black/60 dark:text-white/60"
            >
              Prepare records: {transformSummary(spec)} · {records.length} sample record{records.length === 1 ? '' : 's'} {prepOpen ? '▾' : '▸'}
            </button>
            <span className="flex-1" />
            {builderMode === 'simple' && (
              <input
                aria-label="Search Signals"
                value={signalSearch}
                onChange={(e) => setSignalSearch(e.target.value)}
                placeholder="Search Signals"
                className="w-40 rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-2 py-1 text-[12px] outline-none focus:border-blue-500"
              />
            )}
            <div role="group" aria-label="Builder view" className="flex rounded-lg border border-black/15 dark:border-white/15 text-[12px]">
              {(['simple', 'advanced'] as const).map((m) => (
                <button
                  key={m}
                  aria-pressed={builderMode === m}
                  onClick={() => setBuilderMode(m)}
                  className={`px-2.5 py-1 ${builderMode === m ? 'font-semibold' : 'text-black/50 dark:text-white/50'}`}
                >
                  {m === 'simple' ? 'Simple' : 'Advanced'}
                </button>
              ))}
            </div>
          </div>
          {(builderMode === 'advanced' || prepOpen) && (
            <TransformEditor
              key={`tf-${epoch}`}
              transform={spec.transform ?? {}}
              paths={paths.map((p) => p.path)}
              onChange={(t) => update(setTransform(spec, t))}
            />
          )}
          {builderMode === 'simple' ? (
            <>
              <SignalTable rows={shownRows} selected={selectedRow} onSelect={setSimpleSel} />
              <div className="flex flex-wrap items-center gap-x-2 text-[12px] text-black/50 dark:text-white/50">
                {shownRows.length !== signalRows.length && <span>{shownRows.length} of {signalRows.length} shown ·</span>}
                {signalRows.length > 5 && <span>scroll, no pages ·</span>}
                <button
                  className="text-emerald-600 dark:text-emerald-400"
                  onClick={() => {
                    update(addOutput(spec));
                    setSimpleSel(outputs.length);
                    setSignalSearch('');
                  }}
                >
                  + Add Signal
                </button>
              </div>
              <div className="relative flex items-center gap-2">
                {firstError && (
                  <Button
                    onClick={() => {
                      setSignalSearch('');
                      setSimpleSel(firstError.index);
                    }}
                  >
                    Fix {firstError.name}
                  </Button>
                )}
                <button aria-label="More actions" aria-expanded={moreOpen} onClick={() => setMoreOpen((v) => !v)} className="rounded-lg border border-black/15 dark:border-white/15 px-2.5 py-1 text-sm">
                  ⋯
                </button>
                {moreOpen && (
                  <div role="menu" className="absolute left-0 top-full z-10 mt-1 flex min-w-44 flex-col rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-neutral-900 p-1 text-[13px] shadow-lg">
                    <button role="menuitem" className="rounded-lg px-2 py-1 text-left hover:bg-black/[0.05] dark:hover:bg-white/[0.07]" onClick={() => { setBuilderMode('advanced'); setMoreOpen(false); }}>
                      Open the full editor
                    </button>
                    {outputs.length > 1 && selectedRow !== null && (
                      <button
                        role="menuitem"
                        className="rounded-lg px-2 py-1 text-left text-red-600 hover:bg-black/[0.05] dark:hover:bg-white/[0.07]"
                        onClick={() => {
                          update(removeOutput(spec, selectedRow));
                          setSimpleSel(null);
                          setMoreOpen(false);
                        }}
                      >
                        Remove “{signalRows[selectedRow]?.name}”
                      </button>
                    )}
                  </div>
                )}
              </div>
              {selectedRow !== null && renderOutput(selectedRow)}
            </>
          ) : (
            <>
              {outputs.length > OUTPUTS_PER_PAGE && (
                <OutputPager
                  total={outputs.length}
                  shown={filteredOutputs.length}
                  page={page}
                  pageCount={pageCount}
                  filter={outFilter}
                  onFilter={(v) => {
                    setOutFilter(v);
                    setOutPage(0);
                  }}
                  onPage={setOutPage}
                />
              )}
              {pageOutputs.map((o) => renderOutput(o))}
              {outputs.length > OUTPUTS_PER_PAGE && pageCount > 1 && (
                <OutputPager total={outputs.length} shown={filteredOutputs.length} page={page} pageCount={pageCount} filter={outFilter} onFilter={null} onPage={setOutPage} />
              )}
              <div>
                <Button variant="bordered" onClick={() => update(addOutput(spec))}>
                  <Plus size={14} className="inline" /> Add another Signal output
                </Button>
              </div>
            </>
          )}
          <LabelsEditor key={epoch} labels={staticLabels} onChange={(l) => update(setStaticLabels(spec, l))} />
        </AppCard>

        {/* Live preview */}
        <AppCard className={pane('preview')} compact>
          <span className={paneTitle}>Live preview</span>
          {!results || !summary ? (
            <p className="text-xs text-black/40 dark:text-white/40 m-0">Paste a sample to see the Signals this draft would emit.</p>
          ) : (
            <>
              <p className="text-xs font-medium m-0" data-testid="preview-summary">
                {summary.records} record{summary.records === 1 ? '' : 's'} tested · {summary.produced} signal{summary.produced === 1 ? '' : 's'} produced ·{' '}
                {summary.rejected} rejected
              </p>
              {summary.failures.length > 0 && (
                <ul className="m-0 pl-4 text-[12px] text-amber-600 dark:text-amber-400">
                  {summary.failures.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              )}
              {(() => {
                const rows = results.flatMap((r) => r.outputs.filter((o) => o.signal).map((o) => ({ r, o })));
                const f = previewFilter.trim().toLowerCase();
                const matching = f ? rows.filter(({ o }) => `${o.signal!.name} ${o.signal!.value} ${o.signal!.labels.join(' ')}`.toLowerCase().includes(f)) : rows;
                const shown = matching.slice(0, PREVIEW_ROWS);
                return (
                  <>
                    {rows.length > PREVIEW_ROWS && (
                      <input
                        aria-label="Filter preview"
                        value={previewFilter}
                        onChange={(e) => setPreviewFilter(e.target.value)}
                        placeholder={`Filter ${rows.length} signals`}
                        className="w-full rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-2 py-1 text-[12px] outline-none focus:border-blue-500"
                      />
                    )}
                    <div className="flex flex-col gap-1.5 max-h-72 overflow-y-auto">
                      {shown.map(({ r, o }) => (
                        <div key={`${r.recordIndex}-${o.outputIndex}`} className="flex flex-wrap items-center gap-1">
                          {results.length > 1 && <span className="text-[11px] text-black/40 dark:text-white/40">#{r.recordIndex + 1}</span>}
                          <Badge className="text-emerald-600 dark:text-emerald-400 border-emerald-600/30">{o.signal!.name}</Badge>
                          {o.signal!.value && <span className="text-[12px] font-mono">{o.signal!.value}</span>}
                          <span className="text-[11px] text-black/40 dark:text-white/40">{o.signal!.atSynthesized ? 'at: now (no timestamp rule)' : o.signal!.at}</span>
                          {o.signal!.labels.map((l, k) => (
                            <Badge key={k}>{l}</Badge>
                          ))}
                        </div>
                      ))}
                    </div>
                    {matching.length > shown.length && (
                      <span className="text-[11px] text-black/40 dark:text-white/40">
                        Showing the first {shown.length} of {matching.length}{f ? ' matches' : ' - filter to narrow'}.
                      </span>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </AppCard>
      </div>
      {anyMapRule && <UnmappedValuesPanel nickname={nickname} epoch={epoch} />}
    </div>
  );
}

// Audits Signals already stored under this mapping's own destination
// channel against its signalRef/name value map(s) - see backend/modules/
// mapping/transitions/activity.go's GetUnmappedValues for exactly what
// "fell through the map" means and why it's accurate even for a `system`/
// host-metrics mapping whose rules actually ran client-side in zagentc.
// Only rendered at all when the draft has at least one such rule with a
// non-empty map (anyMapRule above) - nothing to audit otherwise. Re-fetches
// on `epoch` (bumped on load/reload) so a discarded/reloaded draft doesn't
// show a stale audit from a version that's no longer the one being edited.
function UnmappedValuesPanel({ nickname, epoch }: { nickname: string; epoch: number }) {
  const [result, setResult] = useState<UnmappedValuesResult | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetchUnmappedValues(nickname)
      .then((res) => {
        if (!cancelled) setResult(res);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Unable to load the unmapped-values audit.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nickname, epoch]);

  if (loading || loadError || !result || !result.auditable) return null;

  return (
    <AppCard className="flex flex-col gap-2" compact>
      <span className={paneTitle}>Not mapped</span>
      {result.unmapped.length === 0 ? (
        <p className="text-xs text-black/40 dark:text-white/40 m-0">
          Every value seen in the last {result.checked} Signal{result.checked === 1 ? '' : 's'} in this mapping's destination channel matched a
          map entry.
        </p>
      ) : (
        <>
          <p className="text-xs text-black/45 dark:text-white/45 m-0">
            These raw values, seen in the last {result.checked} Signal{result.checked === 1 ? '' : 's'} in the destination channel, had no map
            entry and fell through as-is - add each to a rule's value map above to route it deliberately.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {result.unmapped.map((u) => (
              <Badge key={u.value} className="font-mono">
                {u.value} · {u.count}
              </Badge>
            ))}
          </div>
        </>
      )}
    </AppCard>
  );
}

const OUTPUTS_PER_PAGE = 10;
const PREVIEW_ROWS = 60;

// Filter + Prev/Next for a mapping with many outputs. `onFilter` null = pager only (the bottom copy).
function OutputPager({ total, shown, page, pageCount, filter, onFilter, onPage }: { total: number; shown: number; page: number; pageCount: number; filter: string; onFilter: ((v: string) => void) | null; onPage: (p: number) => void }) {
  const from = shown === 0 ? 0 : page * OUTPUTS_PER_PAGE + 1;
  const to = Math.min(shown, (page + 1) * OUTPUTS_PER_PAGE);
  return (
    <div className="flex flex-col gap-1.5">
      {onFilter && (
        <input
          aria-label="Filter outputs"
          value={filter}
          onChange={(e) => onFilter(e.target.value)}
          placeholder={`Filter ${total} outputs by name or path`}
          className="w-full rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-2 py-1 text-[12px] outline-none focus:border-blue-500"
        />
      )}
      <div className="flex items-center gap-2 text-[11px] text-black/50 dark:text-white/50">
        <span className="flex-1">
          {shown === 0 ? 'No output matches.' : `Outputs ${from}-${to} of ${shown}${shown !== total ? ` (filtered from ${total})` : ''}`}
        </span>
        <button aria-label="Previous outputs" disabled={page <= 0} onClick={() => onPage(page - 1)} className="rounded-lg border border-black/15 dark:border-white/15 px-2 py-0.5 disabled:opacity-30">
          Prev
        </button>
        <button aria-label="Next outputs" disabled={page >= pageCount - 1} onClick={() => onPage(page + 1)} className="rounded-lg border border-black/15 dark:border-white/15 px-2 py-0.5 disabled:opacity-30">
          Next
        </button>
      </div>
    </div>
  );
}

const CUSTOM_PATH = '__custom_path__';

// Which bucket a rule's raw `to` string falls into, for the target dropdown -
// anything the engine doesn't recognise (backend/internal/mappingengine/
// engine.go's applyOneOutput switch, mirrored here) falls to 'other' so an
// unusual legacy value is shown rather than silently rewritten.
type TargetKind = 'name' | 'value' | 'at' | 'signalRef' | 'labels' | 'other';
function targetKind(to: string | undefined): TargetKind {
  if (to === 'name' || to === 'value' || to === 'at' || to === 'signalRef') return to;
  if (to?.startsWith('labels.')) return 'labels';
  return to ? 'other' : 'name';
}

function RuleRow({
  rule,
  paths,
  records,
  channelSignals,
  onPickChannelSignal,
  selected,
  onSelect,
  onPatch,
  onRemove,
  onMove,
  canUp,
  canDown,
}: {
  rule: MappingRule;
  paths: SourcePath[];
  records: JsonValue[];
  channelSignals: { name: string; number?: string; emoji: string }[];
  onPickChannelSignal: (value: string) => void;
  selected: boolean;
  onSelect: () => void;
  onPatch: (p: Partial<Record<keyof MappingRule, unknown>>) => void;
  onRemove: () => void;
  onMove: (delta: -1 | 1) => void;
  canUp: boolean;
  canDown: boolean;
}) {
  const [mapText, setMapText] = useState(() => mapToLines(rule.map));
  const [labelKey, setLabelKey] = useState(() => (rule.to?.startsWith('labels.') ? rule.to.slice(7) : ''));
  const empty = (v: string) => (v === '' ? undefined : v);
  const kind = targetKind(rule.to);
  const isAt = kind === 'at';
  const isSignalRef = kind === 'signalRef';
  const sourceKind = rule.from !== undefined ? 'from' : rule.const !== undefined ? 'const' : 'from';
  const sourceValue = sourceKind === 'from' ? (rule.from ?? '') : String(rule.const ?? '');
  const label = rule.to || 'field';
  const pathInList = paths.some((p) => p.path === sourceValue);
  const signalInList = channelSignals.some((sg) => (sg.number || sg.name) === sourceValue);

  // A `name`/`signalRef` rule that reads a field with several distinct
  // values (e.g. a host-metrics sample where `name` is `cpu_usage_percent`,
  // `mem_used_percent`, ...) can't point every one of them at the same
  // Signal - each value needs its own target. `rule.map` already resolves
  // per-value (the engine applies it generically, see applyOneOutput), this
  // is just a friendlier editor for it than hand-typing `value=41` lines,
  // reading from every owned template via `channelSignals` (same widened
  // list the dropdown below uses). Missing this was the actual bug behind
  // a signalRef rule that just forwarded a metric's raw name text as if it
  // were already a Signal number - `{from:"name",to:"signalRef"}` with no
  // map, which also silently drops the Signal's own name (engine.go's
  // "signalRef leaves Name blank" behavior) instead of doing anything useful.
  const splitValues = (kind === 'name' || kind === 'signalRef') && sourceKind === 'from' && rule.from ? distinctValues(records, rule.from) : [];
  const showValueMapPicker = splitValues.length > 0 && channelSignals.length > 0;

  return (
    <div
      onFocusCapture={onSelect}
      onClick={onSelect}
      className={`flex flex-col gap-1.5 rounded-xl border p-2 ${selected ? 'border-blue-500' : 'border-black/10 dark:border-white/10'}`}
    >
      <div className="flex items-center gap-1">
        <select
          aria-label="Field"
          value={kind}
          onChange={(e) => {
            const next = e.target.value as TargetKind;
            if (next === 'labels') onPatch({ to: `labels.${labelKey}` });
            else onPatch({ to: next });
          }}
          className={selectCls}
        >
          {(Object.keys(TARGET_META) as (keyof typeof TARGET_META)[]).map((t) => (
            <option key={t} value={t}>
              {TARGET_META[t]}
            </option>
          ))}
          <option value="labels">Label</option>
          {kind === 'other' && <option value="other">{rule.to} (custom)</option>}
        </select>
        {kind === 'labels' && (
          <input
            aria-label={`Label key for ${label}`}
            value={labelKey}
            onChange={(e) => {
              setLabelKey(e.target.value);
              onPatch({ to: `labels.${e.target.value}` });
            }}
            placeholder="key"
            className={inputCls}
          />
        )}
        <button aria-label={`Move ${label} up`} disabled={!canUp} onClick={() => onMove(-1)} className="disabled:opacity-30">
          <ArrowUp size={14} />
        </button>
        <button aria-label={`Move ${label} down`} disabled={!canDown} onClick={() => onMove(1)} className="disabled:opacity-30">
          <ArrowDown size={14} />
        </button>
        <button aria-label={`Remove ${label}`} onClick={onRemove}>
          <Trash2 size={14} />
        </button>
      </div>
      <div className="flex items-center gap-1">
        <select
          aria-label={`Source kind for ${label}`}
          value={sourceKind}
          onChange={(e) => onPatch(e.target.value === 'from' ? { from: sourceValue || '', const: undefined } : { const: sourceValue, from: undefined })}
          className="rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-1 py-1 text-[12px]"
        >
          <option value="from">Path</option>
          <option value="const">Constant</option>
        </select>
        {sourceKind === 'from' && paths.length > 0 ? (
          <select
            aria-label={`Source for ${label}`}
            value={pathInList ? sourceValue : CUSTOM_PATH}
            onChange={(e) => onPatch({ from: e.target.value === CUSTOM_PATH ? '' : e.target.value })}
            className={selectCls}
          >
            <option value={CUSTOM_PATH}>{!pathInList && sourceValue ? `${sourceValue} (custom)` : 'Custom path…'}</option>
            {paths.map((p) => (
              <option key={p.path} value={p.path}>
                {p.path} · {p.sample}
              </option>
            ))}
          </select>
        ) : isSignalRef && sourceKind === 'const' && channelSignals.length > 0 ? (
          <select
            aria-label={`Source for ${label}`}
            value={signalInList ? sourceValue : CUSTOM_PATH}
            onChange={(e) => {
              if (e.target.value === CUSTOM_PATH) return;
              onPatch({ const: e.target.value });
              onPickChannelSignal(e.target.value);
            }}
            className={selectCls}
          >
            <option value={CUSTOM_PATH}>{!signalInList && sourceValue ? `${sourceValue} (custom)` : 'Type a number…'}</option>
            {channelSignals.map((sg, idx) => (
              <option key={`${sg.name}-${idx}`} value={sg.number || sg.name} disabled={!sg.number}>
                {sg.emoji} {sg.name} {sg.number ? `#${sg.number}` : '(no number yet)'}
              </option>
            ))}
          </select>
        ) : (
          <input
            aria-label={`Source for ${label}`}
            value={sourceValue}
            onChange={(e) => onPatch(sourceKind === 'from' ? { from: empty(e.target.value) } : { const: e.target.value })}
            placeholder={sourceKind === 'from' ? 'a.b.c' : 'fixed value'}
            className={inputCls}
          />
        )}
        {(sourceKind === 'from' ? !pathInList && paths.length > 0 : isSignalRef && !signalInList && channelSignals.length > 0) && (
          <input
            aria-label={`Custom source for ${label}`}
            value={sourceValue}
            onChange={(e) => onPatch(sourceKind === 'from' ? { from: empty(e.target.value) } : { const: e.target.value })}
            placeholder={sourceKind === 'from' ? 'a.b.c' : 'signal number'}
            className={inputCls}
          />
        )}
      </div>
      {showValueMapPicker && (
        <div className="flex flex-col gap-1 rounded-xl bg-black/[0.03] dark:bg-white/[0.05] p-2">
          <span className="text-[11px] font-medium">
            Map each “{sourceValue}” value to a Signal ({splitValues.length})
          </span>
          {splitValues.map((v) => {
            const current = String(rule.map?.[v] ?? '');
            return (
              <label key={v} className="flex items-center gap-1.5 text-[12px]">
                <span className="font-mono min-w-[7rem] truncate" title={v}>
                  “{v}” →
                </span>
                <select
                  aria-label={`Signal for ${v}`}
                  value={current}
                  onChange={(e) => {
                    const val = e.target.value;
                    onPatch({ map: { ...(rule.map ?? {}), [v]: val } });
                    if (val) onPickChannelSignal(val);
                  }}
                  className={selectCls}
                >
                  <option value="">Keep “{v}” as-is</option>
                  {channelSignals.map((sg, idx) => {
                    const val = kind === 'signalRef' ? sg.number : sg.name;
                    return (
                      <option key={`${sg.name}-${idx}`} value={val ?? ''} disabled={!val}>
                        {sg.emoji} {sg.name} {sg.number ? `#${sg.number}` : kind === 'signalRef' ? '(no number yet)' : ''}
                      </option>
                    );
                  })}
                </select>
              </label>
            );
          })}
        </div>
      )}
      <details>
        <summary className="text-[11px] text-black/45 dark:text-white/45 cursor-pointer select-none">Default &amp; transforms</summary>
        <div className="flex flex-col gap-1.5 pt-1.5">
          <input
            aria-label={`Default for ${label}`}
            placeholder="default if missing"
            value={rule.default === undefined ? '' : String(rule.default)}
            onChange={(e) => onPatch({ default: empty(e.target.value) })}
            className={inputCls}
          />
          <div className="flex gap-1.5">
            <select
              aria-label={`Cast for ${label}`}
              value={rule.cast ?? ''}
              onChange={(e) => onPatch({ cast: empty(e.target.value) })}
              className="rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-1 py-1 text-[12px]"
            >
              <option value="">no cast</option>
              <option value="number">number</option>
              <option value="string">string</option>
              <option value="bool">bool</option>
            </select>
          </div>
          <OpsEditor
            label={label}
            ops={rule.ops ?? []}
            unitScale={rule.unitScale}
            paths={paths}
            onChange={({ ops, unitScale }) => onPatch({ ops: ops.length > 0 ? ops : undefined, unitScale })}
          />
          {isAt && (
            <>
              <input
                aria-label="Timestamp format"
                list="mapping-timestamp-formats"
                value={rule.timestamp ?? ''}
                onChange={(e) => onPatch({ timestamp: empty(e.target.value) })}
                placeholder="format, e.g. DD.MM.YYYY HH:II:ss"
                spellCheck={false}
                className={inputCls}
              />
              <datalist id="mapping-timestamp-formats">
                {TIMESTAMP_PRESETS.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
              <span className="text-[11px] text-black/45 dark:text-white/45">
                <code>rfc3339</code>, <code>unix_s</code>, <code>unix_ms</code>, or a pattern: <code>YYYY</code>/<code>YY</code> year,{' '}
                <code>MM</code> month, <code>DD</code> day, <code>HH</code> hour, <code>mm</code>/<code>II</code> minute, <code>ss</code> second,{' '}
                <code>SSS</code> fraction, <code>Z</code> zone (<code>+02:00</code>); other characters must match exactly. No zone means UTC.
              </span>
              {!rule.timestamp && <span className="text-[11px] text-amber-600 dark:text-amber-400">Without a format, the time falls back to "now".</span>}
            </>
          )}
          {!showValueMapPicker && (
            <textarea
              aria-label={`Value map for ${label}`}
              placeholder={'value map, one per line:\nsuccess=1\nfailure=0'}
              value={mapText}
              onChange={(e) => setMapText(e.target.value)}
              onBlur={() => mapText !== mapToLines(rule.map) && onPatch({ map: linesToMap(mapText) })}
              rows={2}
              spellCheck={false}
              className={inputCls}
            />
          )}
          <label className="flex items-center gap-1.5 text-[12px]">
            <input type="checkbox" checked={!!rule.required} onChange={(e) => onPatch({ required: e.target.checked ? true : undefined })} />
            Required (drop the Signal if missing)
          </label>
        </div>
      </details>
    </div>
  );
}

function LabelsEditor({ labels, onChange }: { labels: Record<string, string>; onChange: (l: Record<string, string>) => void }) {
  const initial = Object.entries(labels).map(([k, v]) => `${k}=${v}`).join('\n');
  const [text, setText] = useState(initial);
  return (
    <details>
      <summary className="text-[11px] text-black/45 dark:text-white/45 cursor-pointer select-none">Static labels on every Signal</summary>
      <textarea
        aria-label="Static labels"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => text !== initial && onChange(linesToMap(text) ?? {})}
        placeholder={'env=prod\nteam=platform'}
        rows={2}
        spellCheck={false}
        className={`${inputCls} mt-1.5`}
      />
    </details>
  );
}
